import React, { useEffect, useState } from 'react';
import { 
    Container, Box, Typography, TextField, Button, Paper, 
    Grid, Divider, useTheme, useMediaQuery 
} from '@mui/material';
import { Save, ContactPhone, Business, Email, Map, AccessTime } from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';

const textFieldStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
        },
    }
};

const ContactSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [info, setInfo] = useState({ phones: ['', '', '', ''], fax: '', email: '', address: '', hours: '', map: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/admin/contact-info');
        if (res?.data) {
          const data = res.data;
          let phones = ['', '', '', ''];
          if (Array.isArray(data.phones)) {
            phones = data.phones.slice(0, 4).concat(new Array(Math.max(0, 4 - data.phones.length)).fill(''));
          } else if (typeof data.phone === 'string' && data.phone) {
            phones[0] = data.phone;
          }
          setInfo({ ...data, phones, fax: data.fax || '', map: data.map || data.mapLink || '' });
        }
      } catch (e) {
        snackbar('خطا در بارگذاری اطلاعات تماس از سرور.');
      }
    };
    load();
  }, []);

  const handleChange = (e) => setInfo({ ...info, [e.target.name]: e.target.value });
  const handlePhoneChange = (index, value) => {
    const phones = [...(info.phones || ['', '', '', ''])];
    phones[index] = value;
    setInfo({ ...info, phones });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.patch('/contact-info', info);
      successMessage('اطلاعات تماس به‌روزرسانی شد');
    } catch (err) {
      console.error(err);
      snackbar('خطا در ذخیره‌سازی');
    } finally { setLoading(false); }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">تنظیمات اطلاعات تماس</Typography>
      </Box>
      <Paper sx={{ p: isMobile ? 2 : 4, borderRadius: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom mb={2} display="flex" alignItems="center" gap={1}>
          <ContactPhone fontSize="small" color="primary" /> شماره‌های تماس
        </Typography>
        <Grid container spacing={2} mb={3}>
          {[0,1,2,3].map(i => (
            <Grid item xs={12} sm={6} key={i}>
              <TextField 
                label={`شماره تماس ${i+1}`} 
                name={`phone${i+1}`} 
                value={(info.phones && info.phones[i]) || ''} 
                onChange={(e) => handlePhoneChange(i, e.target.value)} 
                fullWidth 
                sx={textFieldStyle}
              />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom mb={2} display="flex" alignItems="center" gap={1}>
          <Business fontSize="small" color="primary" /> آدرس و فکس
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <TextField label="نمابر (فکس)" name="fax" value={info.fax || ''} onChange={handleChange} fullWidth sx={textFieldStyle} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="آدرس پستی" name="address" value={info.address} onChange={handleChange} fullWidth sx={textFieldStyle} multiline rows={2} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary" gutterBottom mb={2} display="flex" alignItems="center" gap={1}>
          <Email fontSize="small" color="primary" /> ایمیل و نقشه
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6}>
            <TextField label="ایمیل پشتیبانی" name="email" value={info.email} onChange={handleChange} fullWidth sx={textFieldStyle} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="ساعات کاری" name="hours" value={info.hours} onChange={handleChange} fullWidth sx={textFieldStyle} />
          </Grid>
          <Grid item xs={12}>
            <TextField label="لینک نقشه کُروکی (iframe یا URL)" name="map" value={info.map || ''} onChange={handleChange} fullWidth sx={textFieldStyle} placeholder="مثال: https://maps.google.com/.../output=embed" />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-end">
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={loading}
            fullWidth={isMobile}
            size="large"
            startIcon={<Save />}
            sx={{ borderRadius: 3, px: 5, py: 1.5 }}
          >
            {loading ? 'در حال ذخیره‌سازی...' : 'ذخیره تغییرات'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ContactSettings;
 
